import { useEffect } from 'react';

import { IssueNotificationRuleDTO, NotificationType, UpsertIssueNotificationRuleRequestDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Control, Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { z } from 'zod';

import { Input, inputVariants } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreatableMultiSelect, MultiSelect } from '@/components/ui/multi-select';
import { Select } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toaster';
import { IssueSeverity } from '@/legacy/models/issue';
import { Utils } from '@/legacy/utils';
import { cn } from '@/lib/utils';
import { getTagsQueryOptions } from '@/queries/assets';
import { getLocationsQueryOptions } from '@/queries/locations';
import { ApiService } from '@/services/api-v2';
import { ISSUE_SEVERITY_OPTIONS } from '@/utils/issue';

const formSchema = z
  .object({
    name: z.string().min(1, 'Please enter a name for this rule'),
    enabled: z.boolean(),
    type: z.nativeEnum(NotificationType),
    severities: z.array(z.nativeEnum(IssueSeverity)).optional().default([]),
    serviceReading: z.number().optional(),
    serviceReadingUnit: z.string().optional(),
    serviceReadingDirection: z.enum(['due', 'overdue']).optional(),
    serviceDate: z.number().optional(),
    serviceDateDirection: z.enum(['due', 'overdue']).optional(),
    tags: z.array(z.string()).optional().default([]),
    location: z.object({ value: z.string().nullable(), label: z.string() }).optional(),
    actionsLocation: z.boolean(),
    actionsEmails: z.boolean(),
    actionsSms: z.boolean(),
    emails: z.array(z.string()).optional().default([]),
    sms: z.array(z.string()).optional().default([]),
  })
  .refine(
    (data) => {
      if (data.type === NotificationType.Servicing) {
        return !!data.serviceReadingDirection;
      }
      return true;
    },
    {
      message: 'Please select a reading direction',
      path: ['serviceReadingDirection'],
    },
  )
  .refine(
    (data) => {
      if (data.type === NotificationType.Servicing && data.serviceReadingDirection) {
        return data.serviceReading !== undefined && data.serviceReading > 0;
      }
      return true;
    },
    {
      message: 'Please provide a reading value',
      path: ['serviceReading'],
    },
  )
  .refine(
    (data) => {
      if (data.type === NotificationType.Servicing) {
        return !!data.serviceDateDirection;
      }
      return true;
    },
    {
      message: 'Please select a schedule date direction',
      path: ['serviceDateDirection'],
    },
  )
  .refine(
    (data) => {
      if (data.type === NotificationType.Servicing && data.serviceDateDirection) {
        return data.serviceDate !== undefined && data.serviceDate > 0 && data.serviceDate <= 365;
      }
      return true;
    },
    {
      message: 'Please enter a valid number less than 365',
      path: ['serviceDate'],
    },
  )
  .refine(
    (data) => {
      return data.actionsLocation || data.actionsEmails || data.actionsSms;
    },
    {
      message: 'Please select at least one action',
      path: ['actionsLocation'],
    },
  )
  .refine(
    (data) => {
      if (data.actionsEmails) {
        return data.emails && data.emails.length > 0;
      }
      return true;
    },
    {
      message: 'Please provide at least one email address',
      path: ['emails'],
    },
  )
  .refine(
    (data) => {
      if (data.actionsSms) {
        return data.sms && data.sms.length > 0;
      }
      return true;
    },
    {
      message: 'Please provide at least one mobile number',
      path: ['sms'],
    },
  )
  .refine(
    (data) => {
      if (data.emails) {
        return data.emails.every((email) => Utils.ValidateEmail(email));
      }
      return true;
    },
    {
      message: 'One or more email addresses are invalid',
      path: ['emails'],
    },
  )
  .refine(
    (data) => {
      if (data.sms) {
        return data.sms.every((smsNum) => Utils.isMobilePhoneValid(`+${Utils.normalizePhoneNumber(smsNum)}`));
      }
      return true;
    },
    {
      message: 'One or more numbers are not valid mobile numbers. Enter mobile numbers in international format (e.g. +64 21 123 4567).',
      path: ['sms'],
    },
  );

type FormInput = z.input<typeof formSchema>;
type FormData = z.output<typeof formSchema>;

interface NotificationRuleSheetProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  rule?: IssueNotificationRuleDTO | null;
  type: NotificationType;
}

export function NotificationRuleSheet({ open, onClose, rule, type }: NotificationRuleSheetProps) {
  const toast = useToast();

  const { data: locationsData } = useQuery({ ...getLocationsQueryOptions(), enabled: open });
  const locations = locationsData?.data;

  const { data: tags } = useQuery({ ...getTagsQueryOptions(), enabled: open });

  const formInstance = useForm<FormInput, unknown, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      enabled: true,
      type: type,
      serviceReadingUnit: 'hrs',
      location: { value: null, label: 'All Locations' },
      actionsLocation: false,
      actionsEmails: type === NotificationType.Servicing,
      actionsSms: false,
      emails: [],
      sms: [],
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = formInstance;

  const watchedActions = watch(['actionsLocation', 'actionsEmails', 'actionsSms']);

  useEffect(() => {
    if (rule) {
      reset({
        name: rule.name,
        enabled: rule.enabled,
        type: rule.notificationType,
        severities: rule.severities || [],
        serviceReading: rule.serviceReading ? Math.abs(rule.serviceReading) : undefined,
        serviceReadingUnit: rule.serviceReadingUnit || 'hrs',
        serviceReadingDirection: rule.serviceReading ? (rule.serviceReading > 0 ? 'due' : 'overdue') : 'due',
        serviceDate: rule.serviceDate ? Math.abs(rule.serviceDate) : undefined,
        serviceDateDirection: rule.serviceDate ? (rule.serviceDate > 0 ? 'due' : 'overdue') : 'due',
        tags: rule.tags || [],
        location: rule.location ? { value: rule.location.id, label: rule.location.name } : { value: null, label: 'All Locations' },
        actionsLocation: rule.emailLocation ?? false,
        actionsEmails: rule.emails?.length > 0 || false,
        actionsSms: rule.sms?.length > 0 || false,
        emails: rule.emails || [],
        sms: rule.sms || [],
      });
    } else {
      reset({
        name: '',
        enabled: true,
        type: type,
        serviceReadingUnit: 'hrs',
        location: { value: null, label: 'All Locations' },
        actionsLocation: false,
        actionsEmails: type === NotificationType.Servicing,
        actionsSms: false,
        emails: [],
        sms: [],
      });
    }
  }, [rule, type, reset]);

  async function handleDeleteRule() {
    if (!rule?.id) return;

    try {
      await ApiService.DeleteIssueNotificationRule({ ruleId: rule.id });
      toast.success('Rule deleted successfully');
      onClose(true);
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    }
  }

  async function onSubmit(data: FormData) {
    try {
      const serviceReading = data.serviceReadingDirection ? data.serviceReading! * (data.serviceReadingDirection === 'overdue' ? -1 : 1) : undefined;
      const serviceDate = data.serviceDateDirection ? data.serviceDate! * (data.serviceDateDirection === 'overdue' ? -1 : 1) : undefined;

      const ruleData: UpsertIssueNotificationRuleRequestDTO = {
        id: rule?.id,
        name: data.name,
        enabled: data.enabled,
        type: data.type,
        severities: data.severities && data.severities.length > 0 ? data.severities : undefined,
        serviceReading: serviceReading || undefined,
        serviceReadingUnit: data.serviceReadingUnit as 'hrs' | 'kms' | undefined,
        serviceDate: serviceDate || undefined,
        tags: data.tags && data.tags.length > 0 ? data.tags : undefined,
        locationId: data.location?.value || undefined,
        actions: {
          location: data.actionsLocation,
          emails: data.actionsEmails,
          sms: data.actionsSms,
        },
        emails: data.emails && data.emails.length > 0 ? data.emails : undefined,
        sms: data.sms && data.sms.length > 0 ? data.sms : undefined,
      };

      await ApiService.UpsertIssueNotificationRule(ruleData);
      toast.success(rule ? 'Rule updated successfully' : 'Rule created successfully');
      onClose(true);
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Failed to save rule');
    }
  }

  const locationOptions =
    locations?.map((location) => ({
      value: location.id,
      label: location.name,
    })) || [];

  const tagOptions =
    tags?.map((tag) => ({
      value: tag.tag,
      label: tag.tag,
    })) || [];

  return (
    <Sheet open={open} onOpenChange={() => onClose()}>
      <SheetContent className="w-full max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{rule ? 'Edit' : 'Create'} Notification Rule</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-4">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <div>
                <Label className="text-sm font-medium">Rule Name</Label>
                <Input {...field} placeholder="Enter rule name" error={!!error} />
                {error && <p className="text-default-red mt-1 text-sm">{error.message}</p>}
              </div>
            )}
          />

          {rule && !rule.enabled && (
            <Controller
              control={control}
              name="enabled"
              render={({ field: { value, onChange } }) => (
                <Label className="flex items-center gap-2">
                  <Switch checked={value} onCheckedChange={onChange} />
                  <span className="text-sm font-medium">Enabled</span>
                </Label>
              )}
            />
          )}

          <hr />

          <div className="flex flex-col gap-4">
            <div className="text-sm">
              <b>IF</b> the following conditions are met:
            </div>

            {type === NotificationType.Issue && (
              <Controller
                control={control}
                name="severities"
                render={({ field: { value, onChange } }) => (
                  <div>
                    <Label className="text-sm font-medium">Issue Severities</Label>
                    <MultiSelect
                      isMulti
                      value={ISSUE_SEVERITY_OPTIONS.filter((option) => value?.includes(option.value))}
                      onChange={(selected) => onChange(selected?.map((s) => s.value) || [])}
                      options={ISSUE_SEVERITY_OPTIONS}
                      placeholder="Select severities (optional)"
                    />
                  </div>
                )}
              />
            )}

            {type === NotificationType.Servicing && (
              <>
                <div className="space-y-4">
                  <h4 className="text-sm font-medium">Schedule Conditions</h4>

                  <div>
                    <Label className="text-sm font-medium">Next Reading</Label>
                    <div className="mt-1">
                      <div className="flex gap-2">
                        <Controller
                          control={control}
                          name="serviceReadingDirection"
                          render={({ field: { value, onChange } }) => (
                            <Select
                              aria-label="Next Reading"
                              value={value ? { label: value === 'due' ? 'Due in' : 'Overdue by', value } : undefined}
                              onChange={(option) => onChange(option?.value)}
                              options={[
                                { label: 'Due in', value: 'due' },
                                { label: 'Overdue by', value: 'overdue' },
                              ]}
                              placeholder="Select..."
                              isClearable
                              className="min-w-[120px]"
                            />
                          )}
                        />

                        <Controller
                          control={control}
                          name="serviceReading"
                          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                            <NumericFormat
                              className={cn(
                                inputVariants({
                                  error: !!error,
                                  hasPrefix: false,
                                  hasSuffix: false,
                                }),
                                'w-20',
                              )}
                              value={value ?? ''}
                              onValueChange={(values) => {
                                onChange(values.floatValue ?? undefined);
                              }}
                              onBlur={onBlur}
                              placeholder="Value"
                              allowNegative={false}
                              decimalScale={0}
                              inputMode="numeric"
                            />
                          )}
                        />

                        <Controller
                          control={control}
                          name="serviceReadingUnit"
                          render={({ field: { value, onChange } }) => (
                            <Select
                              aria-label="Unit"
                              value={{ label: value || 'hrs', value: value || 'hrs' }}
                              onChange={(option) => onChange(option?.value)}
                              options={[
                                { label: 'hrs', value: 'hrs' },
                                { label: 'kms', value: 'kms' },
                              ]}
                              className="min-w-[80px]"
                            />
                          )}
                        />
                      </div>
                      <div className="mt-1 flex gap-2">
                        {formInstance.formState.errors.serviceReadingDirection && (
                          <p className="text-default-red text-sm">{formInstance.formState.errors.serviceReadingDirection.message}</p>
                        )}
                        {formInstance.formState.errors.serviceReading && (
                          <p className="text-default-red text-sm">{formInstance.formState.errors.serviceReading.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Next Schedule Date</Label>
                    <div className="mt-1">
                      <div className="flex items-center gap-2">
                        <Controller
                          control={control}
                          name="serviceDateDirection"
                          render={({ field: { value, onChange } }) => (
                            <Select
                              aria-label="Next Schedule Date"
                              value={value ? { label: value === 'due' ? 'Due in' : 'Overdue by', value } : undefined}
                              onChange={(option) => onChange(option?.value)}
                              options={[
                                { label: 'Due in', value: 'due' },
                                { label: 'Overdue by', value: 'overdue' },
                              ]}
                              placeholder="Select..."
                              isClearable
                              className="min-w-[120px]"
                            />
                          )}
                        />

                        <Controller
                          control={control}
                          name="serviceDate"
                          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                            <NumericFormat
                              className={cn(
                                inputVariants({
                                  error: !!error,
                                  hasPrefix: false,
                                  hasSuffix: false,
                                }),
                                'w-20',
                              )}
                              value={value ?? ''}
                              onValueChange={(values) => {
                                onChange(values.floatValue ?? undefined);
                              }}
                              onBlur={onBlur}
                              placeholder="Days"
                              allowNegative={false}
                              decimalScale={0}
                              inputMode="numeric"
                            />
                          )}
                        />

                        <span className="text-sm">days</span>
                      </div>
                      <div className="mt-1 flex gap-2">
                        {formInstance.formState.errors.serviceDateDirection && (
                          <p className="text-default-red text-sm">{formInstance.formState.errors.serviceDateDirection.message}</p>
                        )}
                        {formInstance.formState.errors.serviceDate && (
                          <p className="text-default-red text-sm">{formInstance.formState.errors.serviceDate.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Controller
              control={control}
              name="tags"
              render={({ field: { value, onChange } }) => (
                <div>
                  <Label className="text-sm font-medium">Asset Tags</Label>
                  <MultiSelect
                    isMulti
                    value={tagOptions.filter((option) => value?.includes(option.value))}
                    onChange={(selected) => onChange(selected?.map((s) => s.value) || [])}
                    options={tagOptions}
                    placeholder="Select tags (optional)"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="location"
              render={({ field: { value, onChange } }) => (
                <div>
                  <Label className="text-sm font-medium">Location</Label>
                  <Select
                    aria-label="Location"
                    value={value?.value ? { label: value.label, value: value.value } : { label: 'All Locations', value: '' }}
                    onChange={(selected) => {
                      onChange(selected?.value ? { value: selected.value, label: selected.label } : { value: null, label: 'All Locations' });
                    }}
                    options={[{ value: '', label: 'All Locations' }, ...locationOptions]}
                    placeholder="Select location"
                    isClearable
                  />
                </div>
              )}
            />
          </div>

          <hr />

          <div className="flex flex-col gap-2">
            <h4 className="mb-2 text-sm font-medium">Perform the following actions:</h4>

            <Controller
              control={control}
              name="actionsLocation"
              render={({ field: { value, onChange } }) => (
                <Label className="flex items-center gap-2">
                  <Switch checked={value} onCheckedChange={onChange} />
                  <span className="text-sm">Send email to location contact</span>
                </Label>
              )}
            />

            <Controller
              control={control}
              name="actionsEmails"
              render={({ field: { value, onChange } }) => (
                <Label className="flex items-center gap-2">
                  <Switch checked={value} onCheckedChange={onChange} />
                  <span className="text-sm">Send email to specific addresses</span>
                </Label>
              )}
            />

            <Controller
              control={control}
              name="actionsSms"
              render={({ field: { value, onChange } }) => (
                <Label className="flex items-center gap-2">
                  <Switch checked={value} onCheckedChange={onChange} />
                  <span className="text-sm">Send SMS</span>
                </Label>
              )}
            />

            {formInstance.formState.errors.actionsLocation && (
              <p className="text-default-red text-sm">{formInstance.formState.errors.actionsLocation.message}</p>
            )}
          </div>

          {watchedActions[1] && <EmailListField control={control} formState={formInstance.formState} />}

          {watchedActions[2] && <SmsListField control={control} formState={formInstance.formState} />}

          <div className="flex justify-end gap-2 pt-4">
            {rule && (
              <Button type="button" variant="destructive" onClick={handleDeleteRule} disabled={isSubmitting}>
                Delete
              </Button>
            )}
            <Button variant="success" type="submit" disabled={isSubmitting} loading={isSubmitting}>
              {rule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function EmailListField({
  control,
  formState,
}: {
  control: Control<FormInput, unknown, FormData>;
  formState: ReturnType<typeof useForm<FormInput, unknown, FormData>>['formState'];
}) {
  return (
    <Controller
      control={control}
      name="emails"
      render={({ field: { value, onChange } }) => (
        <div>
          <Label className="text-sm font-medium">Email Addresses</Label>
          <CreatableMultiSelect<{ label: string; value: string }>
            value={value?.map((email) => ({ label: email, value: email })) || []}
            onChange={(selected) => {
              const emails = selected ? selected.map((item) => item.value) : [];
              onChange(emails);
            }}
            onCreateOption={(inputValue) => {
              if (Utils.ValidateEmail(inputValue) && !value?.includes(inputValue)) {
                onChange([...(value || []), inputValue]);
              }
            }}
            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            placeholder="Type email and press Enter to add..."
            noOptionsMessage={() => 'Type an email address and press Enter'}
            isValidNewOption={(inputValue) => {
              return Utils.ValidateEmail(inputValue) && !value?.includes(inputValue);
            }}
            options={[]}
          />
          {formState.errors.emails && <p className="text-default-red mt-1 text-sm">{formState.errors.emails.message}</p>}
        </div>
      )}
    />
  );
}

function SmsListField({
  control,
  formState,
}: {
  control: Control<FormInput, unknown, FormData>;
  formState: ReturnType<typeof useForm<FormInput, unknown, FormData>>['formState'];
}) {
  return (
    <Controller
      control={control}
      name="sms"
      render={({ field: { value, onChange } }) => (
        <div>
          <Label className="text-sm font-medium">SMS Numbers</Label>
          <CreatableMultiSelect<{ label: string; value: string }>
            value={value?.map((sms) => ({ label: sms, value: sms })) || []}
            onChange={(selected) => {
              const numbers = selected ? selected.map((item) => item.value) : [];
              onChange(numbers);
            }}
            onCreateOption={(inputValue) => {
              const normalizedNumber = Utils.normalizePhoneNumber(inputValue);
              if (Utils.isMobilePhoneValid(`+${normalizedNumber}`) && !value?.includes(inputValue)) {
                onChange([...(value || []), inputValue]);
              }
            }}
            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            placeholder="Type mobile number and press Enter to add..."
            noOptionsMessage={() => 'Type a mobile number and press Enter'}
            isValidNewOption={(inputValue) => {
              const normalizedNumber = Utils.normalizePhoneNumber(inputValue);
              return Utils.isMobilePhoneValid(`+${normalizedNumber}`) && !value?.includes(inputValue);
            }}
            options={[]}
          />
          {formState.errors.sms && <p className="text-default-red mt-1 text-sm">{formState.errors.sms.message}</p>}
        </div>
      )}
    />
  );
}
